import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../common/store/combinedReducer";
import "./header.scss";
import { Button, Header } from "@scuf/common";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import config from "../../../../common/authentication/config";
import AboutUsPopup from "../../../../common/components/AboutUsPopup/AboutUsPopup";
import {
  FeedbackModal,
  FeedbackModalConfirmation,
} from "../../../../common/utility/Feedback/FeedbackModal";
import {
  getFeedbackData,
  setFeedbackStatus,
  setLoading,
} from "../../../store/action";
import feedback from "../../../../common/assets/feedback.png";
import { BellIcon, Tooltip } from "@forge/common";
import { useAuth } from "../../../../common/hooks/useAuth";
import AuthProvider from "../../../../common/authentication/authService";

const HeaderComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    hasAnyRole,
    hasAcademyAccess,
    isPermissionsLoading,
    userInfo,
    hasPermission,
    isAcademyInstructorOnly,
  } = useAuth();

  // Memoized value to determine if Course Admin button should be visible
  // Only show the button when permissions are loaded AND user has academy access
  const shouldShowCourseAdminButton = useMemo(() => {
    const isLoading = isPermissionsLoading();
    const hasAccess = hasAcademyAccess();

    // Only show button when NOT loading AND has access
    return !isLoading && hasAccess;
  }, [isPermissionsLoading, hasAcademyAccess]);

  const userDetail: any = useSelector((state: AppState) => {
    return state.reducer.userInfo;
  });

  const displayName = useMemo(() => {
    const fromStore =
      userDetail?.email ||
      userInfo?.email ||
      userInfo?.preferred_username ||
      userDetail?.name ||
      userInfo?.name;
    if (fromStore) return fromStore;
    // Fallback: decode id_token directly (available on loginFailed page)
    try {
      const tokenInfo: any = AuthProvider().getLoggedInUserInfo();
      return tokenInfo?.email || tokenInfo?.preferred_username || tokenInfo?.name || "";
    } catch {
      return "";
    }
  }, [
    userDetail?.name,
    userDetail?.email,
    userInfo?.name,
    userInfo?.preferred_username,
    userInfo?.email,
  ]);
  const siteId: any = useSelector((state: AppState) => {
    return state.reducer.siteId;
  });

  const feedBackStatus = useSelector((state: AppState) => {
    return state.reducer.feedBackStatus;
  });
  const selectedTenant = useSelector((state: AppState) => {
    return state.reducer.selectedTenant;
  });
  const [aboutUsPopOpen, setAboutUsPopOpen] = useState(false);
  const [feedbackPopOpen, setFeedbackPopOpen] = useState(false);
  const [feedbackSubjectInput, setFeedbackSubject] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackConfirmation, setFeedbackConfirmation] = useState(false);
  // const [submitFeedback, setSubmitFeedback] = useState(false);

  const goToAssistance = () => {
    //console.log("siteId", config.ACADEMY_TENANT_ID, selectedTenant);
    if (selectedTenant === config.ACADEMY_TENANT_ID) {
      // https://digitalprimedev2.honeywell.com/helpservice/Content/Academy/Content/Resources/Station%20Help%20Landing%20Pages/Home%20for%20HA.htm
      window
        .open(
          "/helpservice/Content/Academy/Content/Resources/Station_Help_Landing_Pages/Home_for_HA_UG.htm",
          "",
          "noopener",
        )
        ?.focus();
    } else {
      window
        .open(
          "/helpservice/Content/Resources/Station_Help_Landing_Pages/Home.htm",
          "",
          "noopener",
        )
        ?.focus();
    }
  };

  const goToAcademy = () => {
    window
      .open(
        "https://process.honeywell.com/us/en/services/training",
        "",
        "noopener",
      )
      ?.focus();
  };
  const goToHAC = () => {
    // If the button is visible, we know permissions are loaded and user has access
    // So we can safely navigate
    if (shouldShowCourseAdminButton) {
      // Check permission directly and route to the correct page
      // This avoids new tab permission loading issues by making the decision here
      const hasCoursePermission = !isAcademyInstructorOnly() && hasPermission("dp.twin.course.library.view", true);

      if (hasCoursePermission) {
        window.open("/academy/viewCourseTemplates", "", "noopener")?.focus();
      } else {
        window.open("/academy/iso-file-management", "", "noopener")?.focus();
      }
    } else {
      // This should rarely happen since button shouldn't be clickable if user doesn't have access
      // But as a fallback, redirect to dashboard
      navigate("/dashboard");
    }
  };

  const submitFeedback = () => {
    // const encoder = new TextEncoder(); // UTF-8 encoding
    // const encodedSubject = encoder.encode(feedbackSubjectInput);
    // const encodedDescription = encoder.encode(feedbackText);
    dispatch(
      getFeedbackData({
        feedbackData: {
          subject: feedbackSubjectInput,
          description: feedbackText,
        },
        siteId: siteId,
        emailId: userDetail.email,
        accessToken: localStorage.getItem("access_token"),
      }),
    );
    setFeedbackPopOpen(false);
    setFeedbackSubject("");
    setFeedbackText("");
    dispatch(setLoading(true));
  };

  const handleClose = () => {
    setFeedbackConfirmation(false);
    dispatch(setFeedbackStatus({ status: 0 }));
  };
  useEffect(() => {
    if (feedBackStatus !== 0 && feedBackStatus !== undefined) {
      setFeedbackConfirmation(true);
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(false));
    }
  }, [feedBackStatus]);

  const aboutUsPopup = () => {
    return (
      <AboutUsPopup
        heading="About Honeywell Digital Prime Twin"
        version={`Version ${config.VERSION}`}
        patent="Pat.: honpat.com"
        onClose={() => setAboutUsPopOpen(false)}
      />
    );
  };
  return (
    <Header
      id="header"
      title={"Digital Prime Twin"}
      className="header-wrapper"
      menu={false}
      logoUrl={require("../../../../common/assets/honeywell-logo.png")}
      collapseAt={700}
      data-testid="header-wrapper"
    >
      {/* {selectedView === 'Dashboard' ? */}
      <>
        {/* <span className='content-text'>HEY IT'S NOT LIVE !  IT IS A DEV SYSTEM</span> */}
        <div className="header-item">
          {/* commented out for build */}
          {shouldShowCourseAdminButton && (
            <Header.IconItem
              icon={
                <Button
                  type="primary"
                  size="small"
                  className="course-admin-btn"
                  content="Navigate"
                >
                  Course Admin
                </Button>
              }
              description="HAC Academy"
              data-testid="header-HAC"
              style={{ marginTop: "4%" }}
              onClick={() => goToHAC()}
            ></Header.IconItem>
          )}
          <Tooltip content="Feedback" position="bottom">
            <div className="header-icon-tooltip-wrap">
              <Header.IconItem
                icon={<img src={feedback} alt="Feedback" />}
                description="Feedback"
                className="header-icon-options"
                data-testid="header-Feedback"
                onClick={() => setFeedbackPopOpen(true)}
              />
            </div>
          </Tooltip>
          <Tooltip content="E-Learning" position="bottom">
            <div className="header-icon-tooltip-wrap">
              <Header.IconItem
                icon={<BellIcon className="text-white" style={{ fontSize: "20px", pointerEvents: "none" }} />}
                description="E-Learning"
                aria-label="E-Learning"
                className="header-icon-options"
                data-testid="header-graduation"
                onClick={() => goToAcademy()}
              />
            </div>
          </Tooltip>
          <Tooltip content="Digital Prime Twin User Assistance" position="bottom">
            <div className="header-icon-tooltip-wrap">
              <Header.IconItem
                icon={"badge-help"}
                description=""
                aria-label="Digital Prime Twin User Assistance"
                className="header-icon-options"
                data-testid="header-userAssistance"
                onClick={() => goToAssistance()}
              />
            </div>
          </Tooltip>
          <Header.UserProfile
              firstName={displayName || "User"}
              data-testid="header-userWrapper"
              role="Demolitions"
              id={
                shouldShowCourseAdminButton
                  ? "header-logout-user-icon"
                  : "header-logout"
              }
            >
              <Header.UserProfile.Item
                // disabled={true}
                id="user-name"
                data-testid="header-userName"
                className="user-name"
              >
                {displayName}
              </Header.UserProfile.Item>
              {/* <Header.UserProfile.Item
                // disabled={true}
                id="user-name"
                data-testid="header-userName"
                className="user-name"
                title={userDetail?.customerId}
              >
                {"Tenant : " + userDetail?.customerId}
              </Header.UserProfile.Item> */}
              <Header.UserProfile.Item
                // disabled={true}
                id="about-us"
                data-testid="header-about-us"
                className="logout-button"
                title="about-us-title"
                onClick={() => setAboutUsPopOpen(true)}
              >
                About Us
              </Header.UserProfile.Item>
              <Header.UserProfile.Item
                data-test="logout"
                id="logout-button"
                data-testid="header-logout"
                className="logout-button"
                onClick={() => {
                  localStorage.setItem("logged_out_from", "DP header");
                  navigate("/logout");
                }}
              >
                Log Out
              </Header.UserProfile.Item>
            </Header.UserProfile>
        </div>
      </>
      {/* : null} */}

      {aboutUsPopOpen && (
        <div className="aboutus-overlay">{aboutUsPopup()}</div>
      )}
      {feedbackPopOpen && (
        <FeedbackModal
          setFeedbackPopOpen={setFeedbackPopOpen}
          feedbackPopOpen={feedbackPopOpen}
          feedbackSubjectInput={feedbackSubjectInput}
          setFeedbackSubject={setFeedbackSubject}
          setFeedbackText={setFeedbackText}
          feedbackText={feedbackText}
          submitFeedback={submitFeedback}
        />
      )}
      {feedbackConfirmation && (
        <FeedbackModalConfirmation
          setFeedbackConfirmation={setFeedbackConfirmation}
          feedbackConfirmation={feedbackConfirmation}
          feedBackStatus={feedBackStatus}
          handleClose={handleClose}
        />
      )}
    </Header>
  );
};
export default HeaderComponent;
